/**
 * SoundManager port: plays the selected sound profile with Web Audio. Game sounds are spatial in Unity
 * (PlayAtPosition / PlayLoop, logarithmic rolloff, minDistance 1) and heard by the AudioListener
 * on the main camera, 20 units in front of the level plane; gains are normalised so a sound at
 * the camera centre plays at its full volume. Music streams through an <audio> element.
 */
import { soundEventId } from '../../audio/events.ts';
import { loadLibrary, soundUrl, type SoundLibrary } from '../../audio/library.ts';
import { activeProfile, resolveAssignment, type SoundProfile } from '../../audio/profiles.ts';
import type { V2 } from '../sim/unity.ts';

interface Resolved {
  buffer: AudioBuffer;
  volume: number;
  pitch: number;
}

const LISTENER_Z = 20;
const MUTE_KEY = 'ple.play.muted';
/** random pitch spread so repeated sounds do not machine-gun */
const JITTER: Record<string, number> = { jump: 0.1, wallJump: 0.1, footstep: 0.12, land: 0.08, projectileHit: 0.1, volleyHit: 0.1, crumble: 0.08 };

let ctx: AudioContext | null = null;
const buffers = new Map<string, Promise<AudioBuffer | null>>();

function context(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

function decode(ac: AudioContext, url: string): Promise<AudioBuffer | null> {
  let p = buffers.get(url);
  if (!p) {
    p = fetch(url)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(`${url}: ${res.status}`))))
      .then((data) => ac.decodeAudioData(data))
      .catch((e) => {
        console.warn('sound', url, e);
        buffers.delete(url);
        return null;
      });
    buffers.set(url, p);
  }
  return p;
}

export function saveMuted(m: boolean) {
  try {
    localStorage.setItem(MUTE_KEY, m ? '1' : '0');
  } catch {
    /* private mode */
  }
}

export function loadMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

interface Voice {
  src: AudioBufferSourceNode;
  gain: GainNode;
  pan: StereoPannerNode | null;
}

export class GameAudio {
  private readonly ac: AudioContext | null;
  private readonly out: GainNode | null;
  private lib: SoundLibrary | null = null;
  private profile: SoundProfile | null = null;
  private sounds = new Map<string, Resolved | null>();
  private loops = new Map<string, Voice>();
  private touchedLoops = new Set<string>();
  /** one voice per sound event per frame: a row of crumble blocks or color blocks plays once */
  private playedThisFrame = new Set<string>();
  /** UI sounds asked for before their buffer decoded, with a deadline (performance.now()) */
  private pending = new Map<string, number>();
  private _muted = loadMuted();
  private sfxVolume = 1;
  private musicVolume = 0.6;
  private musicEl: HTMLAudioElement | null = null;
  private musicEvent: string | null = null;
  private musicTimer: ReturnType<typeof setTimeout> | null = null;
  private hidden = false;
  private disposed = false;

  constructor(gameData = '/game-data') {
    this.ac = context();
    this.out = this.ac ? this.ac.createGain() : null;
    if (this.ac && this.out) {
      this.out.connect(this.ac.destination);
      this.applyOutGain();
      this.unlock();
    }
    void Promise.all([loadLibrary(gameData), activeProfile()])
      .then(([lib, profile]) => {
        if (this.disposed) return;
        this.lib = lib;
        this.profile = profile;
        this.sfxVolume = profile?.sfxVolume ?? 1;
        this.musicVolume = profile?.musicVolume ?? 0.6;
        this.applyOutGain();
        this.preload();
        if (this.musicEvent) this.startMusic(this.musicEvent);
      })
      .catch((e) => console.warn('sounds', e));
  }

  get muted() {
    return this._muted;
  }

  setMuted(m: boolean) {
    this._muted = m;
    saveMuted(m);
    this.applyOutGain();
    this.syncMusic();
  }

  /** browsers start the context suspended until a user gesture; call from input handlers */
  unlock() {
    if (this.ac && this.ac.state === 'suspended') void this.ac.resume().catch(() => {});
    if (this.musicEl?.paused && this.shouldPlayMusic()) void this.musicEl.play().catch(() => {});
  }

  suspend(hidden: boolean) {
    this.hidden = hidden;
    this.syncMusic();
    if (!this.ac) return;
    if (hidden) void this.ac.suspend().catch(() => {});
    else this.unlock();
  }

  /** SoundManager.PlayAtPosition. `name` is an event id or a Unity SoundType name. */
  play(name: string, x: number, y: number, listener: V2) {
    const id = soundEventId(name);
    const s = this.sounds.get(id);
    if (!s || this.playedThisFrame.has(id)) return;
    this.playedThisFrame.add(id);
    const spread = JITTER[id] ?? 0;
    const v = this.voice(s.buffer, s.pitch + (Math.random() * 2 - 1) * spread * s.pitch, false);
    if (!v) return;
    this.place(v, s.volume, x, y, listener);
    v.src.onended = () => {
      v.src.disconnect();
      v.gain.disconnect();
      v.pan?.disconnect();
    };
    v.src.start();
  }

  /** a non-spatial sound (menus) */
  playUi(name: string) {
    this.play(name, 0, 0, { x: 0, y: 0 });
  }

  /** playUi, or as soon as the sound has loaded (level start fires before the profile is fetched) */
  playUiSoon(name: string) {
    const id = soundEventId(name);
    if (this.sounds.get(id)) this.playUi(id);
    else if (!this.sounds.has(id)) this.pending.set(id, performance.now() + 4000);
  }

  /** SoundManager.PlayLoop / StopLoop, driven every frame: loops not refreshed by endFrame() stop */
  loop(key: string, name: string, x: number, y: number, listener: V2) {
    this.touchedLoops.add(key);
    const s = this.sounds.get(soundEventId(name));
    if (!s) return;
    let v = this.loops.get(key);
    if (!v) {
      const nv = this.voice(s.buffer, s.pitch, true);
      if (!nv) return;
      nv.src.start();
      this.loops.set(key, nv);
      v = nv;
    }
    this.place(v, s.volume, x, y, listener);
  }

  endFrame() {
    for (const [key, v] of this.loops) {
      if (this.touchedLoops.has(key)) continue;
      this.stopVoice(v);
      this.loops.delete(key);
    }
    this.touchedLoops.clear();
    this.playedThisFrame.clear();
  }

  /** switch the background track to a music event (musicLevel, musicVictory, ...) or stop with null */
  music(event: string | null, delayMs = 0) {
    if (event === this.musicEvent) return;
    this.musicEvent = event;
    if (this.musicTimer) clearTimeout(this.musicTimer);
    this.musicTimer = null;
    if (!event) {
      this.stopMusic();
      return;
    }
    if (delayMs > 0) {
      this.stopMusic();
      this.musicTimer = setTimeout(() => {
        this.musicTimer = null;
        if (this.musicEvent === event) this.startMusic(event);
      }, delayMs);
    } else this.startMusic(event);
  }

  dispose() {
    this.disposed = true;
    for (const v of this.loops.values()) this.stopVoice(v);
    this.loops.clear();
    if (this.musicTimer) clearTimeout(this.musicTimer);
    this.stopMusic();
    this.out?.disconnect();
  }

  private preload() {
    const lib = this.lib;
    const ac = this.ac;
    if (!lib || !ac) return;
    for (const ev of Object.keys(lib.suggestions)) {
      const a = resolveAssignment(lib, this.profile, ev);
      const sound = a.sound ? lib.byId.get(a.sound) : undefined;
      if (!sound || sound.file.startsWith('music/')) {
        this.sounds.set(ev, null);
        continue;
      }
      void decode(ac, soundUrl(lib, sound)).then((buffer) => {
        if (!buffer || this.disposed) return;
        this.sounds.set(ev, { buffer, volume: a.volume, pitch: a.pitch });
        const due = this.pending.get(ev);
        if (due === undefined) return;
        this.pending.delete(ev);
        if (performance.now() < due) this.playUi(ev);
      });
    }
  }

  private startMusic(event: string) {
    this.stopMusic();
    const lib = this.lib;
    if (!lib || typeof Audio === 'undefined') return;
    const a = resolveAssignment(lib, this.profile, event);
    const sound = a.sound ? lib.byId.get(a.sound) : undefined;
    if (!sound) return;
    const el = new Audio(soundUrl(lib, sound));
    el.loop = true;
    el.preload = 'auto';
    el.volume = Math.min(1, Math.max(0, a.volume * this.musicVolume));
    el.playbackRate = a.pitch;
    (el as HTMLAudioElement & { preservesPitch?: boolean }).preservesPitch = false;
    this.musicEl = el;
    this.syncMusic();
  }

  private stopMusic() {
    if (!this.musicEl) return;
    this.musicEl.pause();
    this.musicEl.removeAttribute('src');
    this.musicEl.load();
    this.musicEl = null;
  }

  private shouldPlayMusic() {
    return !this._muted && !this.hidden && !this.disposed && this.musicVolume > 0;
  }

  private syncMusic() {
    const el = this.musicEl;
    if (!el) return;
    if (this.shouldPlayMusic()) void el.play().catch(() => {});
    else el.pause();
  }

  private applyOutGain() {
    if (this.out) this.out.gain.value = this._muted ? 0 : this.sfxVolume;
  }

  private voice(buffer: AudioBuffer, rate: number, loop: boolean): Voice | null {
    const ac = this.ac;
    if (!ac || !this.out || ac.state === 'closed') return null;
    const src = ac.createBufferSource();
    src.buffer = buffer;
    src.loop = loop;
    src.playbackRate.value = Math.max(0.01, rate);
    const gain = ac.createGain();
    const pan = typeof ac.createStereoPanner === 'function' ? ac.createStereoPanner() : null;
    src.connect(gain);
    if (pan) {
      gain.connect(pan);
      pan.connect(this.out);
    } else gain.connect(this.out);
    return { src, gain, pan };
  }

  private place(v: Voice, volume: number, x: number, y: number, listener: V2) {
    const dx = x - listener.x, dy = y - listener.y;
    const d = Math.hypot(dx, dy, LISTENER_Z);
    v.gain.gain.value = volume * Math.min(1, LISTENER_Z / d);
    if (v.pan) v.pan.pan.value = Math.max(-1, Math.min(1, dx / d));
  }

  private stopVoice(v: Voice) {
    try {
      v.src.stop();
    } catch {
      /* not started */
    }
    v.src.disconnect();
    v.gain.disconnect();
    v.pan?.disconnect();
  }
}
